import type { KeyboardEvent, ReactElement } from 'react';
import { UseFormRegister } from 'react-hook-form';

import { RadioButton } from '@/component-library/primitives/RadioButton/RadioButton';
import NewEmail from '@/models/NewEmail';

export type OptionSelectorVariant = 'gradient' | 'light';

interface OptionSelectorProps {
  title: string;
  content: string;
  gradientOption: string;
  isSelected: boolean;
  onSelect: (option: string) => void;
  register?: UseFormRegister<NewEmail>;
  radioBtnValue?: string;
  optionImage?: ReactElement;
  disabled?: boolean;
  variant?: OptionSelectorVariant;
}

const OptionSelector: React.FC<OptionSelectorProps> = ({
  title,
  content,
  gradientOption,
  isSelected,
  onSelect,
  register,
  radioBtnValue,
  optionImage,
  disabled = false,
  variant = 'gradient',
}) => {
  const handleSelect = () => {
    onSelect(title);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelect();
    }
  };

  if (variant === 'light') {
    return (
      <div
        role="radio"
        aria-checked={isSelected}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={onKeyDown}
        className={`relative flex h-full min-h-[11.5rem] w-full cursor-pointer flex-col rounded-xl border p-4 text-left shadow-[2px_2px_4px_rgba(0,0,0,0.03)] transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-custom-blue/35 focus-visible:ring-offset-2 ${
          isSelected
            ? 'border-custom-blue/45 bg-custom-blue/[0.07] ring-1 ring-custom-blue/20'
            : 'border-custom-lightSilver bg-white hover:border-custom-mediumSilver hover:shadow-[0_4px_14px_rgba(1,3,11,0.06)]'
        } ${disabled ? 'pointer-events-none opacity-60' : ''}`}
        onClick={handleSelect}
      >
        <div className="flex gap-3">
          <span
            className={`mt-0.5 flex h-[1.125rem] w-[1.125rem] shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
              isSelected ? 'border-custom-blue bg-custom-blue' : 'border-custom-mediumSilver bg-white'
            }`}
            aria-hidden
          >
            {isSelected && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold leading-snug text-custom-black">{title}</h3>
            <p className="mt-2 text-xs font-normal leading-relaxed text-custom-darkSilver">{content}</p>
          </div>
        </div>
        {disabled && (
          <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-white/80 backdrop-blur-[2px]">
            <span className="text-sm font-medium text-custom-darkSilver">Coming soon</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`relative flex w-1/3 flex-col justify-end rounded-[1.356rem] border-transparent p-4 ${
        isSelected ? '' : 'opacity-50'
      } h-[13.188rem] cursor-pointer ${disabled && 'pointer-events-none opacity-100'}`}
      onClick={handleSelect}
      style={{
        backgroundImage: `url('/images/optionGradient${gradientOption}.svg')`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
      }}
    >
      {register && radioBtnValue && (
        <RadioButton {...register('editingType')} value={radioBtnValue} className="w-fit" borderWhite />
      )}
      <div className={`${optionImage ? 'mt-[35px]' : 'mt-[90px]'} flex h-[100%] flex-col align-baseline`}>
        {optionImage && optionImage}
        <h1 className="w-[80%] text-sm font-normal leading-4 tracking-[0.02em] text-white">{title}</h1>
        <p className="mt-2 w-[92%] text-xs font-normal leading-3 tracking-[0.02em] text-gray-100">{content}</p>
      </div>
      {disabled && (
        <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center bg-modalOpacityBG backdrop-blur-[1px]">
          <span className="text-white">Coming soon</span>
        </div>
      )}
    </div>
  );
};

export default OptionSelector;
